import { useGetIdentity } from "ra-core";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Layers,
  DollarSign,
  Users,
  Timer,
  Sprout,
  Leaf,
  Truck,
} from "lucide-react";

import { OwnerMappingConfig } from "../batches/OwnerMappingConfig";
import { RateCardsConfig } from "./RateCardsConfig";
import { DealTypesConfig } from "./DealTypesConfig";
import { EntityTypesConfig } from "./EntityTypesConfig";
import { CropsConfig } from "./CropsConfig";
import { ChannelsConfig } from "./ChannelsConfig";
import { StageLimitsConfig } from "./StageLimitsConfig";

type Section =
  | "deal-types"
  | "stages"
  | "entity-types"
  | "crops"
  | "pricing"
  | "channels"
  | "ownership";

const sections: {
  id: Section;
  label: string;
  group: string;
  icon: typeof Layers;
}[] = [
  { id: "deal-types", label: "Deal Types", group: "Pipeline", icon: Layers },
  { id: "stages", label: "Stages & Limits", group: "Pipeline", icon: Timer },
  {
    id: "entity-types",
    label: "Entity Types",
    group: "Pricing",
    icon: Sprout,
  },
  { id: "crops", label: "Crops", group: "Pricing", icon: Leaf },
  { id: "pricing", label: "Rate Cards", group: "Pricing", icon: DollarSign },
  { id: "channels", label: "Channels", group: "Ownership", icon: Truck },
  { id: "ownership", label: "Owner Mapping", group: "Ownership", icon: Users },
];

export const AdminSettingsPage = () => {
  const { identity } = useGetIdentity();
  const [activeSection, setActiveSection] = useState<Section>("deal-types");

  if (!identity) return null;
  if (!identity.administrator) {
    return (
      <div className="max-w-lg mx-auto mt-8 text-muted-foreground">
        You do not have access to this page.
      </div>
    );
  }

  const groups = [...new Set(sections.map((s) => s.group))];

  return (
    <div className="max-w-5xl mx-auto mt-8">
      <h1 className="text-2xl font-bold mb-6">Admin Settings</h1>
      <div className="flex gap-6">
        {/* Sidebar */}
        <nav className="w-48 shrink-0">
          {groups.map((group) => (
            <div key={group} className="mb-4">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1 px-3">
                {group}
              </h3>
              {sections
                .filter((s) => s.group === group)
                .map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  );
                })}
            </div>
          ))}
        </nav>

        {/* Content */}
        <Card className="flex-1">
          <CardHeader>
            <CardTitle>
              {sections.find((s) => s.id === activeSection)?.label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeSection === "deal-types" && <DealTypesConfig />}
            {activeSection === "stages" && <StageLimitsConfig />}
            {activeSection === "entity-types" && <EntityTypesConfig />}
            {activeSection === "crops" && <CropsConfig />}
            {activeSection === "pricing" && <RateCardsConfig />}
            {activeSection === "channels" && <ChannelsConfig />}
            {activeSection === "ownership" && <OwnerMappingConfig />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

AdminSettingsPage.path = "/admin-settings";
